import { WHERE_OPTIONS } from './navRoutes'

const AVAILABILITY_OPTIONS = [
  'On by default',
  'Turn on in organization settings',
]

const AUDIENCES = ['Owners', 'Box office', 'Finance']

const slugifyTitle = (title) =>
  String(title || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

const normalizeTitle = (title) =>
  String(title || '')
    .trim()
    .toLowerCase()

const cleanSteps = (steps) =>
  (Array.isArray(steps) ? steps : [])
    .map((step) => String(step || '').trim())
    .filter(Boolean)

const extensionOf = (filePath) => {
  const match = String(filePath || '')
    .toLowerCase()
    .match(/\.([a-z0-9]+)$/)
  return match ? match[1] : ''
}

const classifyMedia = (mediaPaths) => {
  const paths = Array.isArray(mediaPaths) ? mediaPaths : []
  return {
    video:
      paths.find((filePath) =>
        ['mp4', 'webm'].includes(extensionOf(filePath))
      ) || null,
    images: paths.filter((filePath) => extensionOf(filePath) === 'png'),
    pdfs: paths.filter((filePath) => extensionOf(filePath) === 'pdf'),
  }
}

const fileNameOf = (filePath) =>
  String(filePath || '')
    .split('/')
    .pop()

const titleTaken = (title, existingTitles, existingSlugs) => {
  const normalized = normalizeTitle(title)
  const slug = slugifyTitle(title)
  const titles = existingTitles || []
  if (titles.some((item) => normalizeTitle(item) === normalized)) return true
  if (!slug) return false
  if (titles.some((item) => slugifyTitle(item) === slug)) return true
  return (existingSlugs || []).some(
    (item) => String(item || '').trim() === slug
  )
}

const featureChecks = ({ input, existingTitles, existingSlugs }) => {
  const title = String(input?.title || '').trim()
  const summary = String(input?.summary || '').trim()
  const steps = cleanSteps(input?.steps)
  const audiences = Array.isArray(input?.audiences) ? input.audiences : []
  const actionLabel = String(input?.actionLabel || '').trim()
  const actionPath = String(input?.actionPath || '').trim()
  const suggestions = Array.isArray(input?.suggestions) ? input.suggestions : []
  const mediaPaths = Array.isArray(input?.mediaPaths) ? input.mediaPaths : []
  const pendingFiles = Array.isArray(input?.pendingFiles)
    ? input.pendingFiles
    : []
  const hasFile =
    mediaPaths.length > 0 ||
    pendingFiles.some((file) => file?.included !== false)

  return [
    {
      id: 'title',
      label: 'Title is present and 60 characters or fewer',
      pass: title.length > 0 && title.length <= 60,
    },
    {
      id: 'unique-title',
      label: 'No other feature has the same title or id',
      pass:
        title.length > 0 && !titleTaken(title, existingTitles, existingSlugs),
    },
    {
      id: 'summary',
      label: 'Summary is present and 160 characters or fewer',
      pass: summary.length > 0 && summary.length <= 160,
    },
    {
      id: 'steps',
      label: 'At least one non-empty step',
      pass: steps.length > 0,
    },
    {
      id: 'file',
      label: 'At least one file is selected',
      pass: hasFile,
    },
    {
      id: 'audience',
      label: 'At least one audience is selected',
      pass:
        audiences.length > 0 &&
        audiences.every((audience) => AUDIENCES.includes(audience)),
    },
    {
      id: 'button',
      label:
        'Button label is present and the link is one of the page suggestions',
      pass: actionLabel.length > 0 && suggestions.includes(actionPath),
    },
  ]
}

const checksPass = (checks) => checks.every((check) => check.pass)

const idForSegment = (pathname, segment) => {
  const match = String(pathname || '').match(
    new RegExp(`(?:^|/)${segment}/([^/]+)`)
  )
  return match ? match[1] : ''
}

const resolveActionPath = (actionPath, pathname) => {
  const path = String(actionPath || '')
  if (!path.includes(':uuid')) return path || '/'
  const segment = path.includes('/flex-package/:uuid')
    ? 'flex-package'
    : path.includes('/package/:uuid')
      ? 'package'
      : path.includes('/event/:uuid')
        ? 'event'
        : ''
  const id = segment ? idForSegment(pathname, segment) : ''
  if (!id) return '/'
  return path.replaceAll(':uuid', id)
}

const formatReleaseDate = (value) => {
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const GUIDE_EXTENSIONS = new Set(['mp4', 'webm', 'png', 'pdf'])

const sanitizeFolder = (folder) => {
  const slug = slugifyTitle(folder)
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) ? slug : ''
}

const sanitizeGuideFilename = (name) => {
  const base = String(name || 'file')
    .split(/[/\\]/)
    .pop()
  const match = base.match(/\.([a-zA-Z0-9]+)$/)
  const ext = match ? match[1].toLowerCase() : ''
  if (!GUIDE_EXTENSIONS.has(ext)) return ''
  const stem = base
    .slice(0, base.length - ext.length - 1)
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
  if (!stem || stem.includes('..')) return ''
  return `${stem}.${ext}`
}

const guideMediaPath = (folder, name) => {
  const safeFolder = sanitizeFolder(folder)
  const safeName = sanitizeGuideFilename(name)
  if (!safeFolder || !safeName) return ''
  return `/guides/${safeFolder}/${safeName}`
}

const previewRelease = (releases, today = new Date()) => {
  const day = today.toISOString().slice(0, 10)
  const list = Array.isArray(releases) ? releases : []
  const existing = list.some(
    (release) => String(release.publishedOn || '').slice(0, 10) === day
  )
  return { publishedOn: day, isNew: !existing }
}

export {
  AUDIENCES,
  AVAILABILITY_OPTIONS,
  WHERE_OPTIONS,
  checksPass,
  classifyMedia,
  cleanSteps,
  featureChecks,
  fileNameOf,
  formatReleaseDate,
  guideMediaPath,
  previewRelease,
  resolveActionPath,
  sanitizeFolder,
  sanitizeGuideFilename,
  slugifyTitle,
}
